"use client";

import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  BadgeCheck, 
  AlertTriangle, 
  Calendar, 
  Clock, 
  Shield, 
  Zap, 
  CornerDownRight 
} from "lucide-react";
import { joomlaVersions } from './joomla-version';

// Import shadcn components
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const JoomlaVersionDashboard = () => {
  const [userVersion, setUserVersion] = useState('');
  const [availableVersions, setAvailableVersions] = useState([]);
  const [versionInfo, setVersionInfo] = useState(null);
  const [latestVersion, setLatestVersion] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [infoIndex, setInfoIndex] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [stats, setStats] = useState({
    totalReleases: 0,
    majorReleases: 0,
    releasesPerMajorVersion: {},
    avgDaysBetweenReleases: 0,
    avgDaysBetweenMajorReleases: 0,
    longestSupportedVersion: '',
    shortestSupportedVersion: '',
    avgSupportDuration: 0
  });

  // Random information scrolls about Joomla
  const infoMessages = [
    "Joomla powers approximately 2.7% of all websites worldwide.",
    "Regular updates improve your site's security against vulnerabilities.",
    "Newer Joomla versions typically have better performance and load times.",
    "The name Joomla comes from a Swahili word meaning 'all together'.",
    "Joomla 4.x introduced improved accessibility features.",
    "Many outdated extensions are incompatible with newer Joomla versions.",
    "Joomla has won multiple CMS awards since its launch in 2005.",
    "The Joomla community has over 700,000 members worldwide.",
    "Each major Joomla version introduces significant architectural improvements.",
    "Security patches are only available for supported versions of Joomla.",
    "Joomla is open-source software, meaning it's free to use and modify.",
    "Joomla supports over 70 languages, making it a truly global CMS.",
    "Joomla 4.x introduced a modern user interface with a cleaner design.",
    "Joomla's extension directory offers over 8,000 free and paid extensions.",
    "Joomla is used by governments, universities, and large organizations worldwide.",
    "Joomla 3.x reached its end of life (EOL) in August 2023.",
    "Joomla's template system allows for highly customizable website designs.",
    "Joomla's built-in caching system improves website performance significantly.",
    "Joomla 4.x introduced a new media manager for easier file management.",
    "Joomla's multilingual features make it ideal for global websites.",
    "Joomla's SEO tools help improve your website's search engine rankings.",
    "Joomla's update system makes it easy to keep your site secure and up-to-date.",
    "Joomla's user management system allows for granular access control.",
    "Joomla's modular architecture makes it highly extensible and flexible.",
    "Joomla's community-driven development ensures continuous innovation.",
    "Joomla's documentation and forums are great resources for troubleshooting.",
    "Joomla's CLI (Command Line Interface) allows for advanced site management.",
    "Joomla's built-in CAPTCHA helps protect your site from spam and bots.",
    "Joomla's workflow feature allows for collaborative content creation.",
    "Joomla's version history reflects its commitment to innovation and security."
];

  // Format date in a nice readable format
  const formatNiceDate = (dateStr) => {
    if (!dateStr || dateStr === "TBD") return "TBD";
    
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Process the Joomla version data
  useEffect(() => {
    const processData = () => {
      try {
        // Sort by version number for proper display
        const sortedData = [...joomlaVersions].sort((a, b) => {
          const versionA = a.version.split('.').map(num => parseInt(num, 10));
          const versionB = b.version.split('.').map(num => parseInt(num, 10));
          
          for (let i = 0; i < Math.max(versionA.length, versionB.length); i++) {
            const numA = versionA[i] || 0;
            const numB = versionB[i] || 0;
            if (numA !== numB) return numA - numB;
          }
          return 0;
        });
        
        // Extract available versions for the dropdown
        const versions = sortedData
          .filter(item => item.status !== "Upcoming")
          .map(item => item.version);
        setAvailableVersions(versions);
        
        // Find the latest stable version
        const stable = sortedData.filter(item => 
          item.status === "Active" && 
          item.release_date !== "TBD" &&
          !item.version.includes("alpha") && 
          !item.version.includes("beta") && 
          !item.version.includes("rc")
        );
        
        if (stable.length > 0) {
          const latest = stable.reduce((prev, current) => {
            const prevVer = prev.version.split('.').map(num => parseInt(num, 10));
            const currVer = current.version.split('.').map(num => parseInt(num, 10));
            
            for (let i = 0; i < Math.max(prevVer.length, currVer.length); i++) {
              const numPrev = prevVer[i] || 0;
              const numCurr = currVer[i] || 0;
              if (numPrev !== numCurr) return numCurr > numPrev ? current : prev;
            }
            return prev;
          });
          
          setLatestVersion(latest);
        }
        
        // Calculate various statistics
        const releasedVersions = sortedData.filter(item => item.release_date !== "TBD");
        
        // Total number of releases
        const totalReleases = releasedVersions.length;
        
        // Major releases - FIXED to only count 1.0.0, 2.5.0, 3.0.0, 4.0.0, 5.0.0
        const majorReleases = releasedVersions.filter(item => {
          // Only consider x.0.0 where x is 1, 2, 3, 4, 5 and 2.5.0 as special case
          const parts = item.version.split('.');
          if (parts[0] === '2' && parts[1] === '5' && parts[2] === '0') return true;
          return parts[1] === '0' && parts[2] === '0' && parseInt(parts[0]) <= 5;
        });
        
        // Count releases per major version (e.g., 3.x, 4.x)
        const releasesPerMajorVersion = {};
        releasedVersions.forEach(item => {
          const majorPart = item.version.split('.')[0];
          releasesPerMajorVersion[majorPart] = (releasesPerMajorVersion[majorPart] || 0) + 1;
        });
        
        // Average days between releases
        let totalDaysBetweenReleases = 0;
        let releaseCount = 0;
        
        for (let i = 1; i < releasedVersions.length; i++) {
          const currentDate = new Date(releasedVersions[i].release_date);
          const prevDate = new Date(releasedVersions[i-1].release_date);
          const diffDays = Math.round((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays > 0) {
            totalDaysBetweenReleases += diffDays;
            releaseCount++;
          }
        }
        
        const avgDaysBetweenReleases = Math.round(totalDaysBetweenReleases / releaseCount) || 0;
        
        // Average days between major releases
        let totalDaysBetweenMajorReleases = 0;
        let majorReleaseCount = 0;
        
        for (let i = 1; i < majorReleases.length; i++) {
          const currentDate = new Date(majorReleases[i].release_date);
          const prevDate = new Date(majorReleases[i-1].release_date);
          const diffDays = Math.round((currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays > 0) {
            totalDaysBetweenMajorReleases += diffDays;
            majorReleaseCount++;
          }
        }
        
        const avgDaysBetweenMajorReleases = Math.round(totalDaysBetweenMajorReleases / majorReleaseCount) || 0;
        
        // Support duration stats
        const versionsWithEol = releasedVersions.filter(v => v.eol_date !== "TBD");
        let longestSupportDuration = 0;
        let longestSupportedVersion = '';
        let shortestSupportDuration = Infinity;
        let shortestSupportedVersion = '';
        let totalSupportDuration = 0;
        
        versionsWithEol.forEach(v => {
          const releaseDate = new Date(v.release_date);
          const eolDate = new Date(v.eol_date);
          const supportDuration = Math.round((eolDate.getTime() - releaseDate.getTime()) / (1000 * 60 * 60 * 24));
          
          totalSupportDuration += supportDuration;
          
          if (supportDuration > longestSupportDuration) {
            longestSupportDuration = supportDuration;
            longestSupportedVersion = v.version;
          }
          
          if (supportDuration < shortestSupportDuration) {
            shortestSupportDuration = supportDuration;
            shortestSupportedVersion = v.version;
          }
        });
        
        const avgSupportDuration = Math.round(totalSupportDuration / versionsWithEol.length);
        
        setStats({
          totalReleases,
          majorReleases: majorReleases.length,
          releasesPerMajorVersion,
          avgDaysBetweenReleases,
          avgDaysBetweenMajorReleases,
          longestSupportedVersion,
          shortestSupportedVersion,
          avgSupportDuration
        });
        
        setDataLoaded(true);
      } catch (error) {
        console.error('Error processing data:', error);
        setDataLoaded(true); // Set data loaded even if there's an error to show error state
      }
    };
    
    processData();
    
    // Set up the info message rotation
    const infoTimer = setInterval(() => {
      setInfoIndex(prevIndex => (prevIndex + 1) % infoMessages.length);
    }, 7000);
    
    return () => clearInterval(infoTimer);
  }, []);
  
  // Update info message when infoIndex changes
  useEffect(() => {
    setInfoMessage(infoMessages[infoIndex]);
  }, [infoIndex, infoMessages]);

  // Handle user version selection
  const handleVersionChange = (value) => {
    setUserVersion(value);
    
    if (value) {
      const versionData = joomlaVersions.find(item => item.version === value);
      setVersionInfo(versionData || null);
    } else {
      setVersionInfo(null);
    }
  };
  
  // Generate lifecycle data for the chart
  const generateLifecycleData = (version) => {
    if (!version) return [];
    
    const releaseDate = new Date(version.release_date);
    const eolDate = version.eol_date !== "TBD" ? new Date(version.eol_date) : null;
    const today = new Date();
    
    const data = [];
    
    // Release point
    data.push({
      name: "Release",
      date: releaseDate.toISOString().split('T')[0],
      value: 0,
      event: "Released"
    });
    
    // Today point
    data.push({
      name: "Today",
      date: today.toISOString().split('T')[0],
      value: Math.round((today.getTime() - releaseDate.getTime()) / (1000 * 60 * 60 * 24)),
      event: "Today"
    });
    
    // EOL point
    if (eolDate) {
      data.push({
        name: "EOL",
        date: eolDate.toISOString().split('T')[0],
        value: Math.round((eolDate.getTime() - releaseDate.getTime()) / (1000 * 60 * 60 * 24)),
        event: "End of Life"
      });
    }
    
    return data;
  };
  
  // Generate upgrade benefits
  const generateUpgradeBenefits = () => {
    if (!versionInfo || !latestVersion) return [];
    
    const versionParts = versionInfo.version.split('.');
    const latestParts = latestVersion.version.split('.');
    
    const benefits = [];
    
    // Major version upgrade benefits
    if (parseInt(versionParts[0]) < parseInt(latestParts[0])) {
      benefits.push({
        icon: <Shield className="h-6 w-6 text-blue-500" />,
        title: "Major Security Improvements",
        description: "Complete architecture overhaul with modern security practices"
      });
      benefits.push({
        icon: <Zap className="h-6 w-6 text-blue-500" />,
        title: "Performance Boost",
        description: "Significant performance improvements through code optimization"
      });
    }
    
    // Any version benefits
    benefits.push({
      icon: <AlertTriangle className="h-6 w-6 text-red-500" />,
      title: "Security Vulnerabilities Fixed",
      description: "Protection against known security threats and vulnerabilities"
    });
    
    benefits.push({
      icon: <BadgeCheck className="h-6 w-6 text-green-500" />,
      title: "Compatible with Latest Extensions",
      description: "Access to the newest extensions and templates in the ecosystem"
    });
    
    return benefits;
  };

  // Calculate EOL status
  const calculateEOLStatus = (version) => {
    const versionData = joomlaVersions.find(v => v.version === version);
    
    if (!versionData || versionData.eol_date === "TBD") {
      return { days: 0, isEol: false, message: "No EOL date available" };
    }
    
    const today = new Date();
    const eolDate = new Date(versionData.eol_date);
    const diffTime = eolDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { 
        days: Math.abs(diffDays), 
        isEol: true, 
        message: `This version reached end-of-life ${Math.abs(diffDays)} days ago` 
      };
    } else {
      return { 
        days: diffDays, 
        isEol: false, 
        message: `This version will reach end-of-life in ${diffDays} days` 
      };
    }
  };

  // Generate release frequency data for chart
  const generateReleaseFrequencyData = () => {
    const majorVersions = Object.keys(stats.releasesPerMajorVersion).sort();
    return majorVersions.map(version => ({
      version: `Joomla ${version}.x`,
      releases: stats.releasesPerMajorVersion[version]
    }));
  };

  // Show loading state if data is not yet loaded
  if (!dataLoaded) {
    return (
      <div className="container mx-auto px-4 py-4 max-w-6xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-center">Joomla Version Health Dashboard</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading Joomla version data...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-0 py-4 max-w-7xl">
      <Card className='border-none shadow-none'>
        <CardHeader>
           {/* Info message ticker */}
           <Alert className="mb-6 bg-blue-50 border-blue-200 text-blue-800">
            <CornerDownRight className="h-5 w-5 text-blue-500" />
            <AlertDescription>{infoMessage}</AlertDescription>
          </Alert>
        </CardHeader>
        
        <CardContent>
          {/* User version selection */}
          <div className="mb-6">
            <label htmlFor="version-select" className="block mb-2 font-medium">
              Select Your Joomla Version:
            </label>
            <Select onValueChange={handleVersionChange} value={userVersion}>
              <SelectTrigger id="version-select" className="w-full">
                <SelectValue placeholder="-- Select Version --" />
              </SelectTrigger>
              <SelectContent>
                {availableVersions.map(version => (
                  <SelectItem key={version} value={version}>{version}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Version status */}
          {versionInfo && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-4">Version Status</h2>
              
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-1">
                      <Calendar className="h-5 w-5 mr-2 text-muted-foreground" />
                      <span className="font-medium">Release Date:</span>
                    </div>
                    <p className="text-lg">{formatNiceDate(versionInfo.release_date)}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-1">
                      <Clock className="h-5 w-5 mr-2 text-muted-foreground" />
                      <span className="font-medium">End of Life Date:</span>
                    </div>
                    <p className="text-lg">{formatNiceDate(versionInfo.eol_date)}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center mb-1">
                      <Shield className="h-5 w-5 mr-2 text-muted-foreground" />
                      <span className="font-medium">Status:</span>
                    </div>
                    <p>
                      {versionInfo.status === "EOL" ? (
                        <Badge variant="destructive">{versionInfo.status}</Badge>
                      ) : versionInfo.status === "Active" ? (
                        <Badge variant="success">{versionInfo.status}</Badge>
                      ) : (
                        <Badge variant="secondary">{versionInfo.status}</Badge>
                      )}
                    </p>
                  </CardContent>
                </Card>
              </div>
              
              {/* EOL Status Alert */}
              {versionInfo.status === "EOL" ? (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>End of Life Reached</AlertTitle>
                  <AlertDescription>
                    Your version has reached end of life {calculateEOLStatus(versionInfo.version).days} days ago.
                    This version no longer receives security updates or bug fixes.
                  </AlertDescription>
                </Alert>
              ) : versionInfo.eol_date !== "TBD" && calculateEOLStatus(versionInfo.version).days < 90 ? (
                <Alert variant="warning" className="mb-4 border-amber-300 bg-amber-50">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-600">Approaching End of Life</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    Your version will reach end of life in {calculateEOLStatus(versionInfo.version).days} days.
                    Consider upgrading soon to ensure continued security updates.
                  </AlertDescription>
                </Alert>
              ) : versionInfo.status === "Active" ? (
                <Alert variant="success" className="mb-4 border-green-300 bg-green-50">
                  <BadgeCheck className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-600">Actively Supported</AlertTitle>
                  <AlertDescription className="text-green-700">
                    Your version is currently supported and receiving updates.
                    {latestVersion && versionInfo.version !== latestVersion.version && 
                      " However, a newer version is available."}
                  </AlertDescription>
                </Alert>
              ) : null}
              
              {/* Version lifecycle visualization */}
              <h3 className="text-lg font-medium mb-2">Version Lifecycle</h3>
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={generateLifecycleData(versionInfo)}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis label={{ value: 'Days', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value, name) => [value + ' days', name]} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="value" 
                          stroke="#8884d8" 
                          activeDot={{ r: 8 }}
                          name="Lifecycle Timeline" 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Upgrade Benefits */}
              {latestVersion && versionInfo.version !== latestVersion.version && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium mb-3">Benefits of Upgrading to {latestVersion.version}</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {generateUpgradeBenefits().map((benefit, index) => (
                      <Card key={index} className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center mb-2">
                            {benefit.icon}
                            <span className="ml-2 font-medium">{benefit.title}</span>
                          </div>
                          <p className="text-sm text-gray-700">{benefit.description}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Call to Action for Outdated Versions */}
              {latestVersion && versionInfo.version !== latestVersion.version && (
                <Card className="bg-blue-50 border-blue-200 mb-6">
                  <CardHeader>
                    <CardTitle className="text-blue-800">Ready to Upgrade?</CardTitle>
                    <CardDescription className="text-blue-700">
                      Upgrading to Joomla {latestVersion.version} will give you access to the latest features, 
                      security improvements, and continued support.
                    </CardDescription>
                  </CardHeader>
                  <CardFooter>
                    <a href="/contact/">
                      <Button className="bg-blue-600 hover:bg-blue-700">
                        Get Upgrade Assistance
                      </Button>
                    </a>
                  </CardFooter>
                </Card>
              )}
            </div>
          )}
          
          {/* Statistics Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Joomla Release Statistics</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-1 text-blue-800">Total Releases</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.totalReleases}</p>
                  <p className="text-sm text-blue-700 mt-1">Released versions to date</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-1 text-blue-800">Major Releases</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.majorReleases}</p>
                  <p className="text-sm text-blue-700 mt-1">Major version milestones</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-1 text-blue-800">Average Support</h3>
                  <p className="text-3xl font-bold text-blue-600">{Math.round(stats.avgSupportDuration / 365 * 10) / 10} years</p>
                  <p className="text-sm text-blue-700 mt-1">{stats.avgSupportDuration} days on average</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-1 text-blue-800">Release Frequency</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.avgDaysBetweenReleases} days</p>
                  <p className="text-sm text-blue-700 mt-1">Average time between releases</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-1 text-blue-800">Major Release Cycle</h3>
                  <p className="text-3xl font-bold text-blue-600">{Math.round(stats.avgDaysBetweenMajorReleases / 30)} months</p>
                  <p className="text-sm text-blue-700 mt-1">{stats.avgDaysBetweenMajorReleases} days on average</p>
                </CardContent>
              </Card>
              
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="text-lg font-medium mb-1 text-blue-800">Longest Support</h3>
                  <p className="text-3xl font-bold text-blue-600">{stats.longestSupportedVersion}</p>
                  <p className="text-sm text-blue-700 mt-1">Had the longest support period</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Release Frequency Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Releases per Major Version</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={generateReleaseFrequencyData()}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="version" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="releases" fill="#8884d8" name="Number of Releases" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </CardContent>
        
        <CardFooter className="flex items-center justify-center border-t pt-6">
          <p className="text-sm text-muted-foreground">
            Data last updated: {new Date().toLocaleDateString()} • This dashboard provides information about Joomla version history and support status
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default JoomlaVersionDashboard;